import type {ContextProvider, ContextProviderDataGeneric} from '@bitstillery/common/lib/context'
import {MithrilComponent} from '@bitstillery/mithril'
import {place_filters} from '@bitstillery/common/lib/filters'
import {CollectionFilterControl, FiltersActive, Icon} from '@bitstillery/common/components'
import {classes} from '@bitstillery/common/lib/utils'
import {$s, $t} from '@bitstillery/common/app'
import m from '@bitstillery/mithril'

import type {CollectionProxy} from '@bitstillery/common/lib/collection'

interface PanelFiltersAttrs {
    className?: string
    collection: CollectionProxy
    context?: ContextProvider<ContextProviderDataGeneric>
    no_float?: boolean
}

export class PanelFilters extends MithrilComponent<PanelFiltersAttrs> {
    view(vnode: m.Vnode<PanelFiltersAttrs>) {
        /** Portal shop: bottom sheet (mobile) or left drawer (tablet) — teal shell SCSS matches cart overlay. */
        const portal_shop_sheet = Boolean(vnode.attrs.no_float && ['mobile', 'tablet'].includes($s.env.layout))

        return (
            <div
                className={classes('c-panel-filters', vnode.attrs.className, {
                    'c-panel-filters--mobile-shop-overlay': portal_shop_sheet,
                    collapsed: $s.panels.view.filters.collapsed,
                    disabled: vnode.attrs.collection.state.bulk_mode || vnode.attrs.collection.state.inline_edit_mode,
                    'float-panel': !vnode.attrs.no_float,
                    'float-panel--active': !vnode.attrs.no_float && ['mobile', 'tablet'].includes($s.env.layout),
                })}
            >
                {/* Gated on there being filters to place rather than on the collection being ready: which
                    filters a page has is configuration, so a page that hands them over early (the shop
                    does, for its server render) gets its panel in the first paint instead of an empty
                    column that fills in on hydration. Their options and counts still arrive with the
                    data. For every other caller this is the same moment — `init` assigns the filters and
                    turns the collection ready in one go. */}
                {Object.keys(vnode.attrs.collection.filters).length > 0 && [
                    portal_shop_sheet ? (
                        <div className='panel-title'>
                            <Icon name='filter' size='d' type='unset' />
                            <span>{$t('filters.panel_title')}</span>
                        </div>
                    ) : null,
                    !portal_shop_sheet ? (
                        <header
                            onclick={() => {
                                // Portal shop (`no_float`): the header is a plain label row — there's no
                                // collapsed panel look on desktop, and the tablet sheet reads
                                // `filters.collapsed` as its open state; don't flip it from here.
                                if (vnode.attrs.no_float) {
                                    return
                                }
                                $s.panels.view.filters.collapsed = !$s.panels.view.filters.collapsed
                            }}
                        >
                            <div className='title'>
                                <div className='icon'>
                                    <Icon className='icon-d' name='filter' />
                                </div>
                                <span>{$t('filters.panel_title')}</span>
                            </div>
                            {/* Portal shop desktop: the expand/reset pair joins the title as one cluster —
                                filter actions live with the filters they act on. Sheet layouts keep the
                                pair in the thumb bar below. */}
                            {vnode.attrs.no_float ? (
                                <CollectionFilterControl collection={vnode.attrs.collection} labels={true} />
                            ) : null}
                        </header>
                    ) : null,

                    !portal_shop_sheet && $s.env.layout === 'mobile' && !vnode.attrs.no_float ? (
                        <CollectionFilterControl collection={vnode.attrs.collection} />
                    ) : null,
                    portal_shop_sheet ? (
                        <div className='c-panel-filters-mobile-sheet-controls'>
                            <button
                                aria-label={$t('filters.sheet_collapse_tip')}
                                className='c-panel-filters-mobile-sheet-collapse-bar'
                                type='button'
                                onclick={() => {
                                    $s.panels.view.filters.collapsed = true
                                }}
                            >
                                <Icon name={$s.env.layout === 'tablet' ? 'chevronLeft' : 'chevronDown'} size='s' type='unset' />
                            </button>
                        </div>
                    ) : null,

                    // Every surface: a search term is the one filter with no card of its own in the list
                    // below, and several can be active at once. Sits directly above the list so it stays
                    // put while the cards scroll.
                    <FiltersActive collection={vnode.attrs.collection} />,

                    <div className='scroll-container'>
                        {vnode.children}
                        {place_filters(vnode.attrs.collection)}
                    </div>,

                    portal_shop_sheet ? (
                        <footer className='c-panel-filters-thumb-bar'>
                            <CollectionFilterControl collection={vnode.attrs.collection} />
                        </footer>
                    ) : null,
                ]}
            </div>
        )
    }
}
